import ReactGA from 'react-ga4';
import { useMemo } from 'react';
import { Card, Box, Typography, Divider, CardContent } from '@mui/material';
import { Offering, Package } from '@revenuecat/purchases-js';
import { Button, useGetIdentity } from 'react-admin';
import { PeriodDurationType, ProductOfferingMetadata } from '../../payWall/paywall.model';
import { authProvider } from '../../auth/authProvider';

interface Props {
  rcOffering: Offering;
  duration: PeriodDurationType;
  onClick?: (rcPackage: Package) => void;
}

export const ProductOfferingCard = ({ rcOffering, duration, onClick }: Props) => {

  const { identity, isLoading: identityLoading } = useGetIdentity();

  const product = rcOffering[duration]?.webBillingProduct;
  const rcPackage = rcOffering[duration];

  const metadata = rcOffering.metadata as any as ProductOfferingMetadata;

  const freePackage = useMemo(() => {
    return metadata?.free ?? false;
  }, [rcOffering]);

  const onSelect = async () => {
    // Send event to Google Analytics when user selects a product
    ReactGA.event('select_product', {
      category: 'Pricing',
      action: 'Select Product',
      label: product?.title || rcOffering.identifier,
      value: price,
    });

    if (!product || freePackage || !rcPackage ||
      (identityLoading || (!identity?.id))
    ) {
      // Handle free product selection
      // User is not logged in, redirect to login
      await authProvider.login({});

    } else if (onClick) {
      onClick(rcPackage);
    }
  };

  const price = useMemo(() => {
    if (metadata.free) return '0';
    if (!product || !product.price) return 'N/A';
    return String(product.price.amountMicros / 1000000)
  }, [product]);

  if (freePackage && (identityLoading || (identity?.id))) {
    return null;
  }

  return (
    <Card
      sx={{
        minHeight: 52,
        width: 300,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: '#333333',
        p: 2,
      }}
    >
      <CardContent sx={{
        textAlign: 'center',
        gap: 2,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 400,
        justifyContent: 'space-between'
      }}>
        <Typography typography='h6'>{rcOffering.serverDescription}</Typography>
        <Box display='flex' flexDirection='row' justifyContent='center' alignItems='center' gap={1}>
          <Typography variant='body1'>{'$'}</Typography>
          <Typography variant='h3'>{price}</Typography>
        </Box>

        <Typography variant='body2'>
          {metadata.mainDescription}
        </Typography>
        <Typography variant='body2'>
          {metadata.validFor}
        </Typography>

        <Button
          sx={{
            paddingBlock: 1.5,
            backgroundColor: 'primary.main',
            color: '#000000',
            border: '1px solid #FFFFFF',
            '&:hover': {
              backgroundColor: '#000000',
              color: '#FFFFFF',
            },
            textTransform: 'none'
          }}
          onClick={onSelect}
        >
          Select
        </Button>
      </CardContent>
      <Divider sx={{ my: 2 }} />
      <Box display='flex' flexDirection='column' gap={2}>
        {metadata.features.map((feature, index) => (
          <Typography key={index} variant='body2'>{feature}</Typography>
        ))}
      </Box>

    </Card>
  )
};
